import React, { useState } from "react";
import { Map as MapIcon, Hash, Maximize, Swords, ExternalLink, Search } from "lucide-react";
import useFetch from "@/lib/useFetch";
import { PageHeader, Footer } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import Countdown from "@/components/common/Countdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MapPreview from "@/components/common/MapPreview";

// RustMaps renders an interactive pan/zoom map with monuments for any seed + size.
function rustMapUrl(size, seed) {
    return `https://rustmaps.com/map/${size}_${seed}`;
}

export default function Maps() {
    const { data } = useFetch("/maps");
    const [seed, setSeed] = useState("");
    const [size, setSize] = useState("4000");
    if (!data) return <Loader />;

    const openLookup = () => {
        if (!seed.trim()) return;
        window.open(rustMapUrl(size.trim() || "4000", seed.trim()), "_blank", "noopener");
    };

    return (
        <div data-testid="player-maps">
            <PageHeader title="Maps" subtitle="Server maps, seeds, monuments and wipe schedules. Open any seed as an interactive map." />

            {/* Seed lookup -> interactive RustMaps */}
            <div data-testid="map-seed-lookup" className="tactical-card rounded-lg p-4 mb-6">
                <div className="label-mono text-muted-foreground mb-2 flex items-center gap-1"><Search size={12} /> Open Interactive Map by Seed</div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid="map-seed-input" value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="Map seed (e.g. 184726354)" className="bg-background border-border" />
                    <Input data-testid="map-size-input" value={size} onChange={(e) => setSize(e.target.value)} placeholder="Size (e.g. 4000)" className="bg-background border-border sm:w-40" />
                    <Button data-testid="map-lookup-open" onClick={openLookup} className="font-heading uppercase bg-hazard text-black hover:bg-hazard/90">
                        <ExternalLink size={15} className="mr-1" /> Open Map
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Opens an interactive map on RustMaps.com. Procedural (PC) seeds render monuments and biomes; console seeds may differ.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {data.map((m) => (
                    <div key={m.community_key} data-testid={`map-${m.community_key}`} className="tactical-card rounded-lg p-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <MapIcon size={18} className="text-hazard" />
                                <span className="font-heading text-lg font-bold uppercase text-foreground">{m.community_name}</span>
                            </div>
                            {m.monument_pvp && <StatusBadge status="failed" label="MONUMENT PVP" />}
                        </div>
                        <div className="label-mono text-muted-foreground mt-1">{m.server_name}</div>
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <Info icon={Maximize} label="Map Size" value={m.map_size} />
                            <Info icon={Hash} label="Seed" value={m.map_seed} />
                        </div>
                        <div className="mt-4">
                            <MapPreview size={m.map_size} seed={m.map_seed} testId={`map-preview-${m.community_key}`} />
                        </div>
                        <div className="mt-4 rounded-md border border-border bg-background/40 p-3">
                            <Countdown target={m.save_wipe} label="Next Save Wipe" />
                        </div>
                        <a href={rustMapUrl(m.map_size, m.map_seed)} target="_blank" rel="noreferrer"
                            data-testid={`view-map-${m.community_key}`}
                            className="mt-3 flex items-center justify-center gap-2 rounded-md border border-hazard/40 bg-hazard/10 px-3 py-2 text-sm font-heading uppercase text-hazard hover:bg-hazard/20 transition-colors">
                            <ExternalLink size={14} /> View Interactive Map
                        </a>
                        <div className="mt-4">
                            <div className="label-mono text-muted-foreground mb-2 flex items-center gap-1"><Swords size={12} /> Monuments</div>
                            <div className="flex flex-wrap gap-1">
                                {m.monuments.map((mon) => (
                                    <span key={mon} className={`label-mono rounded-sm border px-1.5 py-0.5 ${m.monument_pvp && ["Oil Rig", "Cargo Ship", "Launch Site"].includes(mon) ? "border-danger/40 text-danger" : "border-border text-muted-foreground"}`}>{mon}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Footer />
        </div>
    );
}

function Info({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2">
            <Icon size={14} className="text-muted-foreground" />
            <div><div className="label-mono text-muted-foreground">{label}</div><div className="font-heading font-bold text-foreground">{value}</div></div>
        </div>
    );
}
